
    import Component from "vue-class-component";
    import {Prop} from "vue-property-decorator";
    import {
        ApplicationActions,
        EditDocumentSeedRequest,
        EditEndorsementSeedRequest,
        EndorsementDocumentApiModel,
        EndorsementPositionModel,
        EndorsementRestrictionModel,
        factory,
        LanguageOnPrintedEndorsement,
        PositionOption,
        PositionsClient,
        RestrictionOption,
        RestrictionsClient,
        StcwRulesClient,
        StcwRuleLightModel, ResultError,
    } from "@/api-client";
    import IssueDocumentInnerComponent
        from "@/features/applications/components/issue-document/IssueDocumentInnerComponent";
    import DatePicker from "@/components/DatePicker.vue";
    import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
    import EndorsementPositionModal
        from "@/features/applications/components/issue-document/EndorsementPositionModal.vue";
    import {BvTableFieldArray} from "bootstrap-vue";
    import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
    import EndorsementOverallRestrictionsModal
        from "@/features/applications/components/issue-document/EndorsementOverallRestrictionsModal.vue";
    import IssueDocumentSignature from "@/features/applications/components/issue-document/IssueDocumentSignature.vue";
    import LabelOptional from "@/features/shared/components/LabelOptional.vue";
    import AdditionalInfoButton from "@/features/shared/components/AdditionalInfoButton.vue";
    import PopoverInfo from '@/components/PopoverInfo.vue';
    import moment from 'moment';

    @Component({
        components: {
            AdditionalInfoButton,
            DatePicker,
            IssueDocumentSignature,
            HeadingGroup,
            EndorsementPositionModal,
            TableEmptySlot,
            EndorsementOverallRestrictionsModal,
            LabelOptional,
            PopoverInfo
        }
    })
    export default class IssueEndorsement extends IssueDocumentInnerComponent {
        @Prop({required: true})
        issueDocumentDetails!: EndorsementDocumentApiModel
        @Prop({required: true})
        actions!: ApplicationActions;
        @Prop({required: true})
        errors!: ResultError;

        optionsLoaded = false;
        private positionOptions: PositionOption[] = [];
        private restrictionOptions: RestrictionOption[] = [];
        private stcwRulesOptions: StcwRuleLightModel[] = [];

        selectedStcwRules: StcwRuleLightModel[] = [];

        languageOnPrintOptions = LanguageOnPrintedEndorsement;

        validUntil = this.issueDocumentDetails.validUntil || this.issueDocumentDetails.suggestedValidUntil;
        issueDate = this.issueDocumentDetails.issueDate;
        additionalInfo = this.issueDocumentDetails.additionalInfo;
        languageOnPrint = this.issueDocumentDetails.languageOnPrint;
        positions = this.issueDocumentDetails.positions;
        overallRestrictions = this.issueDocumentDetails.overallRestrictions;
        disabledAutomaticRestrictions = this.issueDocumentDetails.disabledAutomaticRestrictions;
        initialEditModel = this.getEditModel();

        $refs!: {
            positionModal: EndorsementPositionModal;
            restrictionsModal: EndorsementOverallRestrictionsModal;
        }

        getEditModel(): EditDocumentSeedRequest {
            return new EditEndorsementSeedRequest({
                additionalInfo: this.additionalInfo,
                issueDate: this.issueDate,
                validUntil: this.validUntil,
                languageOnPrint: this.languageOnPrint,
                positions: this.positions,
                overallRestrictions: this.overallRestrictions,
                disabledAutomaticRestrictions: this.disabledAutomaticRestrictions,
                stcwRules: (this.selectedStcwRules || []).map(rule => rule.id)
            })
        }

        addPosition() {
            this.$refs.positionModal.open(factory.create(EndorsementPositionModel), (model: EndorsementPositionModel) => {
                const existingPosition = this.positions.filter(p => p.id === model.id)[0];
                if (existingPosition) {
                    this.mergePositions(existingPosition, model);
                } else {
                    this.positions.push(model);
                }
            });
        }

        editPosition(position: EndorsementPositionModel) {
            this.$refs.positionModal.open(position, (model: EndorsementPositionModel) => {
                const existingPosition = this.positions.filter(p => p != position && p.id === model.id)[0];
                if (existingPosition) {
                    this.mergePositions(existingPosition, model);
                    this.$delete(this.positions, this.positions.indexOf(position));
                } else {
                    this.$set(this.positions, this.positions.indexOf(position), model);
                }
            });
        }

        editRestrictions() {
            this.$refs.restrictionsModal.open(this.overallRestrictions, (restrictions: EndorsementRestrictionModel[]) => this.overallRestrictions = restrictions);
        }

        private mergePositions(position: EndorsementPositionModel, otherPosition: EndorsementPositionModel) {
            const newRestrictions = position.restrictions.filter(r => !otherPosition.restrictions.some(er => er.id === r.id));
            position.restrictions = otherPosition.restrictions.concat(newRestrictions);
        }

        async created() {
            this.positionOptions = await new PositionsClient().positionOptions();
            this.restrictionOptions = await new RestrictionsClient().list();
            this.stcwRulesOptions = await new StcwRulesClient().list();
            this.optionsLoaded = true;
            this.selectedStcwRules = this.issueDocumentDetails.stcwRules.map(id => this.stcwRulesOptions.filter(rule => rule.id === id)[0]);

            this.$watch(() => this.issueDate, () => {
            if(this.issueDate && moment().startOf("day").diff(this.issueDate) < 0){
                this.errors.clearKey("issueDate");
                this.errors.appendMessage(this.$ls.Global_IssuedOn_In_The_Future,"issueDate");
            }
            else{
                this.errors.clearKey("issueDate");
            }
        });

        this.$watch(() => this.validUntil, () => {
            this.errors.clear();
            if(this.issueDate && this.validUntil){
                this.errors.clearKey("validUntil");
                if(moment(this.validUntil).diff(this.issueDate) <= 0){
                    this.errors.appendMessage(this.$ls.Global_ValidUntil_Before_IssuedOn,"validUntil");
                }
                if(moment(this.validUntil).startOf("day").diff(moment().add(5,"years").startOf("day")) > 0){
                    this.errors.appendMessage(this.$ls.Global_Can_Be_Valid_For_Five_Years,"validUntil");
                }
            }
        });
        }

        removePosition(position: EndorsementPositionModel) {
            this.positions.splice(this.positions.indexOf(position), 1);
        }

        removeRestriction(restriction: EndorsementRestrictionModel) {
            this.overallRestrictions.splice(this.overallRestrictions.indexOf(restriction), 1);
        }

        getPositions() {
            return this.positionOptions;
        }

        getRestrictionsEst(position: EndorsementPositionModel) {
            return position.restrictions.map(restriction => this.getRestrictionDescriptionEst(restriction));
        }

        getRestrictionsEng(position: EndorsementPositionModel) {
            return position.restrictions.map(restriction => this.getRestrictionDescriptionEng(restriction));
        }

        getRestrictionDescriptionEst(restriction: EndorsementRestrictionModel) {
            return restriction.id ? this.restrictionOptions.filter(r => r.value === restriction.id)[0].name.estonian : restriction.descriptionEst;
        }

        getRestrictionDescriptionEng(restriction: EndorsementRestrictionModel) {
            return restriction.id ? this.restrictionOptions.filter(r => r.value === restriction.id)[0].name.english : restriction.descriptionEng;
        }

        toggleAutomaticRestriction(id: string) {
            if (this.disabledAutomaticRestrictions.some(r => r === id)) {
                this.disabledAutomaticRestrictions.splice(this.disabledAutomaticRestrictions.indexOf(id), 1);
            } else {
                this.disabledAutomaticRestrictions.push(id);
            }
        }

        getSeafarerDesiredPositions() {
            return `${this.$ls.Applications_Endorsement_Alert_SeafarerDesiredPositions} ${this.issueDocumentDetails.desiredPositions.join(", ")}`;
        }

        removeStcwRule(rule: StcwRuleLightModel) {
            this.selectedStcwRules.splice(this.selectedStcwRules.indexOf(rule), 1);
        }
        fiveYears(){
            return this.issueDate ? moment(this.issueDate).add(5, 'years').toDate() : moment().add(5, 'years').toDate();
        }
        positionFields: BvTableFieldArray = [
            {
                key: 'position',
                label: this.$ls.Applications_Endorsement_Table_Label_Position,
                sortable: false,
                formatter: (value: any, key: any, item: EndorsementPositionModel) => {
                    return this.getPositions().filter(p => p.value === item.id)[0].text
                }
            },
            {
                key: 'restrictionsEst',
                label: this.$ls.Applications_Endorsement_Table_Label_RestrictionsEst,
                sortable: false
            },
            {
                key: 'restrictionsEng',
                label: this.$ls.Applications_Endorsement_Table_Label_RestrictionsEng,
                sortable: false
            },
            {
                key: 'action',
                label: '',
                sortable: false,
                tdClass:'table-actions'
            },
        ];

        restrictionFields: BvTableFieldArray = [
            {
                key: 'restrictionEst',
                label: this.$ls.Applications_Endorsement_Table_Label_RestrictionEst,
                sortable: false,
                formatter: (value: any, key: any, item: EndorsementRestrictionModel) => {
                    return this.getRestrictionDescriptionEst(item);
                }
            },
            {
                key: 'restrictionEng',
                label: this.$ls.Applications_Endorsement_Table_Label_RestrictionEng,
                sortable: false,
                formatter: (value: any, key: any, item: EndorsementRestrictionModel) => {
                  return this.getRestrictionDescriptionEng(item);
                }
            },
            {
                key: 'action',
                label: '',
                sortable: false,
                tdClass:'table-actions'
            },
        ];
    }
